import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MimeType } from '@repo/shared';

import ClearFilters from '@/components/blocks/data-table/clear-filters';
import TaskCombobox from '@/components/task-combobox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import MultiCombobox from '@/components/ui/multi-combobox';
import { Switch } from '@/components/ui/switch';
import { useFormSearchParams } from '@/hooks/use-form-search-params';
import type { DatasetFiltersSchema } from '@/routes/(app)/(datasets)/lib/schema';

const DatasetFilters = () => {
  const { t } = useTranslation();
  const form = useFormContext<DatasetFiltersSchema>();
  const allValues = form.watch();

  useFormSearchParams('/app/datasets');

  const amountOfActiveFilters = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      Object.values(allValues).filter((value) => value !== undefined).length,
    [allValues]
  );

  const handleClearFilters = useCallback(() => {
    form.reset({
      task: undefined,
      mimeTypes: undefined,
      isEval: true,
    });
  }, [form]);

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-3 gap-x-4 gap-y-2'>
        <FormField
          control={form.control}
          name='task'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('datasets.filter.task')}</FormLabel>
              <FormControl>
                <TaskCombobox {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='mimeTypes'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('datasets.filter.mimetype.label')}</FormLabel>
              <FormControl>
                <MultiCombobox
                  {...field}
                  options={Object.values(MimeType).map((mimeType) => ({
                    label: mimeType,
                    value: mimeType,
                  }))}
                  values={field.value}
                  placeholder={t('datasets.filter.mimetype.placeholder')}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='isEval'
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('datasets.filter.isEval')}</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <ClearFilters
        amountOfActiveFilters={amountOfActiveFilters}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
};

export default DatasetFilters;
